import {
  ConfigurationDetail,
  EncodingError,
  EncodingValidationRequest,
  EncodingValidationResponse
} from 'stylewhere/api'
import { EncodingExtensions as Base } from 'core/extensions/EncodingExtensions'
import { __, T } from 'core/shared/i18n'
import { EncodingOperationConfig } from 'core/shared/RemoteOperation'
import {
  getPreEncodingDuplicateRoleContext,
  PreEncodingDuplicateRole,
  PreEncodingDuplicateRoleContext
} from '../shared/preEncodingCorrection'
import { getMissingPreEncodingBehaviour, GUCCI_MISSING_PREENCODING_ERROR } from '../shared/utils'

const GUCCI_SHOES_EXTENSION_CODES = ['gucciShoesEncoding', 'gucciShoesVariantEncoding']
const GUCCI_SHOES_ERROR_KEYS: Record<string, string> = {
  GUCCI_ERROR_PRODUCT_VARIANT: 'error.GUCCI_SHOES_ERROR_PRODUCT_VARIANT',
  GUCCI_ERROR_PRODUCT_NOT_VARIANT: 'error.GUCCI_SHOES_ERROR_PRODUCT_NOT_VARIANT'
}

export class EncodingExtensions extends Base {
  static getProductionOrderFields = (_operation?: EncodingOperationConfig) => [
    { label: __(T.fields.entity), path: 'attributes.sender' },
    { label: __(T.fields.year), path: 'attributes.year' },
    { label: __(T.fields.number), path: 'attributes.number' },
    { label: __(T.fields.row), path: 'attributes.row' },
    { label: __(T.fields.season), path: 'attributes.season' }
  ]

  /**
   * Override: nella validazione dei tag prima della scelta taglia la configurazione viene richiesta
   * senza work order row, quindi senza startEntityId (il BE risponde con una configurazione fissa).
   */
  static async getWorkOrderRowId(workOrderRowCode, operationId) {
    if (!workOrderRowCode) return undefined
    return super.getWorkOrderRowId(workOrderRowCode, operationId)
  }

  static getBlockingErrorMessage(
    operation: EncodingOperationConfig,
    error: EncodingError,
    res: EncodingValidationResponse
  ): string {
    const customExtensionCode = (operation.options as { customExtensionCode?: string }).customExtensionCode
    const shoesKey =
      customExtensionCode && GUCCI_SHOES_EXTENSION_CODES.includes(customExtensionCode)
        ? GUCCI_SHOES_ERROR_KEYS[error.errorCode]
        : undefined
    return shoesKey ? __(shoesKey) : super.getBlockingErrorMessage(operation, error, res)
  }

  static getPreEncodingDuplicateRoleContext = (
    operation?: EncodingOperationConfig,
    response?: EncodingValidationResponse,
    encodingValidation?: EncodingValidationRequest
  ): PreEncodingDuplicateRoleContext | undefined =>
    getPreEncodingDuplicateRoleContext(operation, response, encodingValidation)

  static getPreEncodingDuplicateRoleAssociationMessage = (duplicateRole: PreEncodingDuplicateRole): string => {
    if (duplicateRole === 'EMBEDDED_R') {
      return __('pre_encoding.association_extra_right')
    }
    return __('pre_encoding.association_extra_left')
  }

  static getPreEncodingDuplicateRoleCorrectionInstruction = (duplicateRole: PreEncodingDuplicateRole): string => {
    if (duplicateRole === 'EMBEDDED_R') {
      return __('pre_encoding.correction_instruction_right')
    }
    return __('pre_encoding.correction_instruction_left')
  }

  static getMissingPreEncodingConfirmationMessage = (error: { ref?: string; payload?: { role?: string } }): string => {
    const role = error.payload?.role ?? error.ref
    if (role === 'EMBEDDED_L') return __('missing_pre_encoding.confirm_left')
    if (role === 'EMBEDDED_R') return __('missing_pre_encoding.confirm_right')
    return __('missing_pre_encoding.confirm')
  }

  private static readonly hasOtherErrorsOnIdentifier = (
    result: EncodingValidationResponse | undefined,
    error: { ref?: string; payload?: { role?: string } },
    identifier: EncodingValidationRequest['identifiers'][number]
  ): boolean =>
    !!result?.errors?.some(
      (candidate) =>
        candidate.errorCode !== GUCCI_MISSING_PREENCODING_ERROR &&
        (candidate.ref === error.ref ||
          candidate.ref === identifier.code ||
          candidate.ref === identifier.tid ||
          candidate.ref === identifier.role ||
          candidate.payload?.role === error.payload?.role ||
          candidate.payload?.role === identifier.role)
    )

  private static readonly findIdentifierIndexForPreEncodingError = (
    identifiers: EncodingValidationRequest['identifiers'],
    error: { ref?: string; payload?: { role?: string } }
  ): number => {
    if (error.ref) {
      const byCodeOrTid = identifiers.findIndex(
        (identifier) => identifier.code === error.ref || identifier.tid === error.ref
      )
      if (byCodeOrTid >= 0) return byCodeOrTid

      const byRole = identifiers.findIndex((identifier) => identifier.role === error.ref)
      if (byRole >= 0) return byRole
    }

    if (error.payload?.role) {
      return identifiers.findIndex((identifier) => identifier.role === error.payload?.role)
    }

    return -1
  }

  static async set_identifiers_status(
    encodingValidation: EncodingValidationRequest,
    result?: EncodingValidationResponse,
    enabledUhfWrite?: boolean,
    expectOnlyMandatoryIdentifiers?: boolean
  ) {
    const updated = await super.set_identifiers_status(
      encodingValidation,
      result,
      enabledUhfWrite,
      expectOnlyMandatoryIdentifiers
    )
    const behaviour = getMissingPreEncodingBehaviour()
    const missingPreEncodingErrors =
      result?.errors?.filter((error) => error.errorCode === GUCCI_MISSING_PREENCODING_ERROR) ?? []

    missingPreEncodingErrors.forEach((error) => {
      const index = EncodingExtensions.findIdentifierIndexForPreEncodingError(updated.identifiers, error)
      if (index < 0) return

      const identifier = updated.identifiers[index]
      const hasOtherErrors = EncodingExtensions.hasOtherErrorsOnIdentifier(result, error, identifier)

      if (behaviour === 'ignore') {
        if (hasOtherErrors) return
        identifier._colorStatus = 'CONFIRMED'
        identifier._status = 'CONFIRMED'
        identifier._error = undefined
        identifier._errorMessage = undefined
        return
      }

      if (behaviour === 'warnWithAutoconfirm' || behaviour === 'warnWithUserConfirmation') {
        if (hasOtherErrors) return
        identifier._colorStatus = undefined
        identifier._status = 'ERROR'
        identifier._error = __('identifier_error.GUCCI_MISSING_PREENCODING')
      }
    })

    return updated
  }

  /**
   * Override: con più tag richiesti sulla stessa coppia identifierType/role arrivano più errori MISSING_TAG,
   * e la find del core li farebbe cadere tutti sullo stesso box (il primo della coppia, tipicamente quello
   * appena letto, che verrebbe retrocesso da PROCESSING a EXPECTED). Qui ogni errore viene assegnato a un box
   * diverso, preferendo quelli non letti.
   */
  static findIdentifierForMissingTagError(
    encodingValidation: EncodingValidationRequest,
    error: EncodingError,
    alreadyAssigned: ConfigurationDetail[]
  ): ConfigurationDetail | undefined {
    const candidates = encodingValidation.identifiers.filter(
      (idt) =>
        idt.identifierType === error.payload.identifierType &&
        idt.role === error.payload.role &&
        idt._errorCode !== 'Not Associated Tag' &&
        !alreadyAssigned.includes(idt)
    )
    // l'errore riguarda un tag mancante, quindi ha senso solo su un box non ancora letto
    return candidates.find((idt) => idt._read === false || idt._status === 'TO_BE_READ') ?? candidates[0]
  }

  /**
   * Override: i placeholder valorizzati con un codice preso dalla response sono marcati _read: false,
   * perché il tag non è stato letto fisicamente. Senza questa esclusione il loop di arricchimento del core
   * li porterebbe a PROCESSING e quindi a CONFIRMED, mostrandoli verdi al primo giro di verify successivo.
   */
  static shouldEnrichIdentifierFromResponse(idf: ConfigurationDetail) {
    return super.shouldEnrichIdentifierFromResponse(idf) && idf._read !== false
  }

  static async verify(
    encodingValidation: EncodingValidationRequest,
    addCodesFromValidationResponse = false,
    runId?: string
  ) {
    const result = await super.verify(encodingValidation, addCodesFromValidationResponse, runId)
    EncodingExtensions.removeDuplicatedIdentifiers(result.encodingValidation)
    return result
  }

  private static removeDuplicatedIdentifiers(encodingValidation: EncodingValidationRequest) {
    const kept = new Map<string, ConfigurationDetail>()
    encodingValidation.identifiers.forEach((idf) => {
      if (!idf.code) return
      const key = `${idf.identifierType}|${idf.code}`
      const current = kept.get(key)
      if (!current || (current._read === false && idf._read !== false)) kept.set(key, idf)
    })
    encodingValidation.identifiers = encodingValidation.identifiers.filter(
      (idf) => !idf.code || kept.get(`${idf.identifierType}|${idf.code}`) === idf
    )
  }

  /**
   * Override: una configurazione Gucci può richiedere più tag con la stessa coppia identifierType/role.
   * Il core ragiona per presenza (un solo placeholder per coppia), qui si ragiona per conteggio:
   * si aggiungono tanti placeholder quanti sono i dettagli obbligatori non ancora coperti,
   * assegnando a ciascuno un codice diverso preso dalla response.
   */
  static addMissingConfigurationIdentifiers(
    encodingValidation: EncodingValidationRequest,
    configurationDetails: ConfigurationDetail[],
    res: EncodingValidationResponse,
    addCodesFromValidationResponse: boolean
  ) {
    // raggruppo i dettagli obbligatori per coppia identifierType/role
    const requiredDetailsByKey = new Map<string, ConfigurationDetail[]>()
    configurationDetails
      .filter((c) => !c.optional)
      .forEach((cd) => {
        const key = `${cd.identifierType}|${cd.role ?? ''}`
        const details = requiredDetailsByKey.get(key)
        if (details) details.push(cd)
        else requiredDetailsByKey.set(key, [cd])
      })

    // codici già presenti, per non riusare lo stesso code su più placeholder della stessa coppia
    const usedCodes = new Set(
      encodingValidation.identifiers.map((idf) => idf.code).filter((code): code is string => !!code)
    )

    requiredDetailsByKey.forEach((details) => {
      const cd = details[0]
      const alreadyPresent = encodingValidation.identifiers.filter(
        (f) => f.identifierType === cd.identifierType && (f.role ?? '') === (cd.role ?? '')
      ).length
      const missing = details.length - alreadyPresent
      if (missing <= 0) return

      // codici disponibili nella response per questa coppia, esclusi quelli già assegnati
      const availableCodes: string[] = []
      if (addCodesFromValidationResponse) {
        res.item.identifiers
          .filter((idt) => idt.type === cd.identifierType && (idt.role ?? '') === (cd.role ?? ''))
          .forEach((idt) => {
            const code = idt.code ?? ''
            if (code && usedCodes.has(code)) return
            availableCodes.push(code)
            if (code) usedCodes.add(code)
          })
      }

      for (let i = 0; i < missing; i++) {
        const identifierToAdd: ConfigurationDetail = {
          identifierType: cd.identifierType,
          role: cd.role ?? '',
          _status: 'TO_BE_READ'
        }
        if (i < availableCodes.length) {
          identifierToAdd.code = availableCodes[i]
          // codice preso dalla response, non letto: resta fuori dal payload di verify e dall'arricchimento
          identifierToAdd._read = false
        }
        encodingValidation.identifiers.push(identifierToAdd)
      }
    })
  }
}
